import { NumberOutlined } from '@ant-design/icons';
import { Button } from 'components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import AuthContainer from '../containers/AuthContainer';

type Props = {
  loading: boolean;
  displayCodeFull: boolean;
  displayCodeExpired: boolean;
  onSubmit: (licenseCode?: string) => void;
  onBack: () => void;
  initialLicenseCode?: string;
};

type FormValues = { license_code: string };

export default function RegisterLicenseCodeStep({
  loading,
  displayCodeFull,
  displayCodeExpired,
  onSubmit,
  onBack,
  initialLicenseCode,
}: Props) {
  const { t } = useTranslation();
  const form = useForm<FormValues>({ mode: 'onChange', defaultValues: { license_code: initialLicenseCode ?? '' } });

  const onFinish = (values: FormValues) => {
    onSubmit(values.license_code || undefined);
  };

  return (
    <AuthContainer
      title={t('loginRegister.licenseCodeStep.title')}
      description=""
      mode="register"
      bottomLinkTo="login"
      displayBackButton
      onBackButtonClick={onBack}
    >
      <Form {...form}>
        <form id="license_code_step" onSubmit={form.handleSubmit(onFinish)} className="flex flex-col gap-4">
          <p>
            <Trans i18nKey="loginRegister.licenseCodeStep.description" components={{ b: <b /> }} />
          </p>
          <FormField
            control={form.control}
            name="license_code"
            render={({ field }) => (
              <FormItem>
                <div className="relative">
                  <NumberOutlined className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground" />
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('loginRegister.fields.licenseCode.placeholder')}
                      className="pl-9 lowercase placeholder:normal-case"
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          {displayCodeFull && (
            <p className="mt-1 mb-0 text-destructive-text">{t('loginRegister.fields.licenseCode.full')}</p>
          )}
          {displayCodeExpired && (
            <p className="mt-1 mb-0 text-destructive-text">{t('loginRegister.fields.licenseCode.expired')}</p>
          )}

          <Button type="submit" size="xl" className="w-full" disabled={loading} loading={loading}>
            {t('loginRegister.licenseCodeStep.submitButton')}
          </Button>
          <div className="mb-12 text-center">
            <Button
              type="button"
              variant="secondary"
              size="xl"
              className="w-full"
              onClick={() => onSubmit(undefined)}
              disabled={loading}
            >
              {t('loginRegister.licenseCodeStep.skipButton')}
            </Button>
          </div>
        </form>
      </Form>
    </AuthContainer>
  );
}
